import { ref } from "vue";
import { PermissionType } from "~~/types/user";

/**
 * Composable for playing ETA reminder sounds
 * Generates an urgent ETA alert beep using Web Audio API
 */
export const useEtaSound = () => {
  let audioContext: AudioContext | null = null;
  let repeatIntervalId: ReturnType<typeof setInterval> | null = null;
  const isRepeating = ref(false);

  /**
   * Initialize audio context (lazy initialization)
   */
  const getAudioContext = (): AudioContext | null => {
    if (!audioContext && typeof window !== "undefined") {
      try {
        audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      } catch (error) {
        console.warn("Web Audio API not supported:", error);
        return null;
      }
    }
    return audioContext;
  };

  /**
   * Play an ETA reminder sound
   * Generates an urgent three-tone alert using Web Audio API
   */
  const playEtaSound = async () => {
    // Check if user has permission to disable ETA reminder sound
    const userStore = useUserStore();
    if (userStore.currentUser?.permissions.includes(PermissionType.DISABLE_ETA_REMINDER_SOUND)) {
      return;
    }

    try {
      const context = getAudioContext();
      if (!context) return;

      // Resume context if suspended (required for Chrome's autoplay policy)
      if (context.state === "suspended") {
        await context.resume();
      }

      const now = context.currentTime;

      // Create three rapid ascending tones for urgency
      const frequencies = [1000, 1200, 1400]; // Higher, more urgent frequencies
      const durations = [0.1, 0.1, 0.15]; // Shorter, more rapid tones

      frequencies.forEach((freq, index) => {
        const oscillator = context.createOscillator();
        const gainNode = context.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(context.destination);

        oscillator.frequency.setValueAtTime(freq, now);
        oscillator.type = "square"; // More piercing sound

        const startTime = now + index * 0.08; // Slight overlap for urgency
        const endTime = startTime + (durations[index] ?? 0.1);

        // Sharp attack and decay for alert feel
        gainNode.gain.setValueAtTime(0, startTime);
        gainNode.gain.linearRampToValueAtTime(0.4, startTime + 0.01);
        gainNode.gain.linearRampToValueAtTime(0, endTime);

        oscillator.start(startTime);
        oscillator.stop(endTime);
      });
    } catch (error) {
      console.error("Error playing ETA sound:", error);
    }
  };

  /**
   * Start repeating the ETA sound every second (more frequent for urgency)
   */
  const startRepeatingSound = () => {
    // Don't start if already repeating
    if (isRepeating.value) {
      return;
    }

    // Play sound immediately
    playEtaSound();

    // Start repeating every second for more urgent ETA alerts
    repeatIntervalId = setInterval(() => {
      playEtaSound();
    }, 1000);

    isRepeating.value = true;
  };

  /**
   * Stop repeating the ETA sound
   */
  const stopRepeatingSound = () => {
    if (repeatIntervalId) {
      clearInterval(repeatIntervalId);
      repeatIntervalId = null;
    }
    isRepeating.value = false;
  };

  return {
    playEtaSound,
    startRepeatingSound,
    stopRepeatingSound,
    isRepeating,
  };
};
